import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { esitoUpload } from "@/lib/upload";
import { Upload, X, Loader2, FileText, Plus } from "lucide-react";

const stripExt = (n) => (n || "").replace(/\.[^.]+$/, "");
const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

export default function UploadDialog({ cliente, onClose, onUploaded, categoria = "Documenti Cliente", categoriaOptions = null, titolo = "Carica documenti", nomeSuggerito = "" }) {
  const inputRef = useRef(null);
  const ultimoDrop = useRef(0);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [genere, setGenere] = useState(categoriaOptions ? categoriaOptions[0] : categoria);
  const [genereCustom, setGenereCustom] = useState("");
  const suffix = ` (${[cliente.cognome, cliente.nome].filter(Boolean).join(" ")})`;

  const handleFiles = (list) => {
    const picked = Array.from(list || []).map((f, i) => ({ uid: uid(), file: f, name: nomeSuggerito && i === 0 ? nomeSuggerito : stripExt(f.name) }));
    if (picked.length) setItems((prev) => [...prev, ...picked]);
  };
  const addFiles = (e) => { handleFiles(e.target.files); if (inputRef.current) inputRef.current.value = ""; };

  const upload = async () => {
    if (items.length === 0) return toast.error("Seleziona almeno un file");
    if (categoriaOptions && genere === "Altro" && !genereCustom.trim())
      return toast.error("Inserisci il nome della cartella");
    const target = categoriaOptions
      ? (genere === "Altro" ? genereCustom.trim() : genere)
      : categoria;
    setBusy(true);
    try {
      const fd = new FormData();
      items.forEach((it) => { fd.append("files", it.file); fd.append("names", it.name || stripExt(it.file.name)); });
      fd.append("categoria", target);
      const { data } = await api.post(`/clienti/${cliente.id}/upload`, fd);
      toast.success(esitoUpload(data));
      onUploaded?.();
      onClose();
    } catch (err) {
      toast.warning(err?.response?.data?.detail || "Upload non riuscito");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#16233c]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="upload-dialog">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-4 border-t-2 border-[#C9A84C] shadow-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl text-[#1A2B4A]">{titolo}</h3>
          <button data-testid="upload-dialog-close" onClick={onClose} className="text-[#1A2B4A] hover:text-[#9b2c2c]"><X className="w-6 h-6" /></button>
        </div>

        {categoriaOptions && (
          <div className="mb-4">
            <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Genere documento (cartella su Drive)</label>
            <select data-testid="upload-genere" value={genere} onChange={(e) => setGenere(e.target.value)}
              className="w-full mt-1 px-3 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C] bg-white">
              {categoriaOptions.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            {genere === "Altro" && (
              <input data-testid="upload-genere-custom" value={genereCustom} onChange={(e) => setGenereCustom(e.target.value)}
                placeholder="Nome cartella (es. Corrispondenza)"
                className="w-full mt-2 px-3 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
            )}
          </div>
        )}

        <div data-testid="upload-pick"
          onDragEnter={(e) => e.preventDefault()} onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
          onDrop={(e) => { e.preventDefault(); e.stopPropagation(); ultimoDrop.current = Date.now(); setDrag(false); handleFiles(e.dataTransfer.files); }}
          onClick={(e) => { if (e.currentTarget.contains(e.target) && Date.now() - ultimoDrop.current > 1500) inputRef.current?.click(); }}
          className={`flex flex-col items-center justify-center gap-1 border border-dashed rounded-sm py-6 cursor-pointer transition-colors ${drag ? "border-[#C9A84C] bg-[#faf5e6]" : "border-[#1A2B4A]/30 hover:border-[#C9A84C]"}`}>
          <Upload className="w-5 h-5 text-[#1A2B4A]" />
          <span className="text-xs text-[#4a5568]">Trascina qui i file, seleziona o scatta una foto</span>
          <input ref={inputRef} data-testid="upload-file-input" type="file" multiple accept="image/*,application/pdf,.doc,.docx" className="hidden" onChange={addFiles} />
        </div>

        {items.length > 0 && (
          <div className="mt-4 space-y-3">
            <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Assegna un nome a ogni documento</p>
            {items.map((it, i) => (
              <div key={it.uid} className="border border-[#1A2B4A]/10 rounded-sm p-3">
                <div className="flex items-center gap-2 text-[11px] text-[#4a5568] mb-1.5">
                  <FileText className="w-3.5 h-3.5" /> <span className="truncate">{it.file.name}</span>
                  <button className="ml-auto" onClick={() => setItems(items.filter((_, j) => j !== i))}><X className="w-3.5 h-3.5 text-[#9b2c2c]" /></button>
                </div>
                <div className="flex gap-2">
                  <input data-testid={`upload-name-${i}`} value={it.name}
                    onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                    placeholder="es. Passaporto"
                    className="flex-1 px-3 py-2 text-sm bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
                  <button title="Aggiungi nome cliente" onClick={() => setItems(items.map((x, j) => j === i ? { ...x, name: (x.name || "") + suffix } : x))}
                    className="inline-flex items-center gap-1 text-xs border border-[#1A2B4A]/20 rounded-sm px-2 hover:border-[#C9A84C]">
                    <Plus className="w-3 h-3" /> cliente
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[#1A2B4A] rounded-sm hover:bg-[#f9f8f5]">Annulla</button>
          <button data-testid="upload-confirm" onClick={upload} disabled={busy}
            className="px-4 py-2 text-sm bg-[#1A2B4A] text-white rounded-sm hover:bg-[#22345c] transition-colors flex items-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Carica
          </button>
        </div>
      </div>
    </div>
  );
}
